import SdkAuth from "@commercetools/sdk-auth";
import type { NextApiRequest } from "next";
import { getToken } from "next-auth/jwt";
import type { JWT } from "next-auth/jwt";
import { FetchServiceAxios } from "../../lib/helpers/fetchServiceAxios";
import { COMMERCETOOLS_ACCESS_TOKEN_URL } from "../../lib/constants";
import type { Credentials, LoginType } from "../api-services/services/auth-service/types";
import type { ApiCustomerResponse } from "@lib/api-services/services/profile-service/types";

export type ConsentVersionsMap = {
    newsletter?: string;
    privacyPolicy?: string;
    termsAndConditions?: string;
};

export type SignUpValues = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    accessToken: string;
    market: string;
    locale: string;
    consentVersions: ConsentVersionsMap;
    phoneticFirstName?: string;
    phoneticLastName?: string;
    dateOfBirth?: string;
};

export const getCommerceToolsClientScopes = (projectKey: string) =>
    `view_products:${projectKey} view_categories:${projectKey} manage_my_shopping_lists:${projectKey} view_stores:${projectKey} manage_my_quote_requests:${projectKey} create_anonymous_token:${projectKey} manage_my_payments:${projectKey} manage_my_quotes:${projectKey} view_published_products:${projectKey} manage_my_profile:${projectKey} manage_my_orders:${projectKey} manage_my_business_units:${projectKey} view_product_selections:${projectKey}`;

const appClientScopes = getCommerceToolsClientScopes(
    process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY || ""
);

// This API client is used to
// * Create anonymous tokens (using basic auth)
// * Get customer info for a customer signing in and there by merging the anonymous cart with the signed in cart
// * Signing in authenticated costumers to get new access token (using basic auth)
// * Refreshing tokens
// * Calling commercetools to get products info and info for the current customer (anonymous or authenticated). Thius is via BFF and the access token is passed in the X-ct-access-token header
//
// Has scopes:
//    view_published_products
//    view_product_selections
//    view_products
//    view_categories
//
//    manage_my_orders
//    manage_my_profile
//    manage_my_quotes
//    manage_my_shopping_lists
//    manage_my_payments
//    manage_my_quote_requests
//    manage_my_business_units
//
//    create_anonymous_token
//
// The access token created using this API client is safe to expose to the current user as the scope only allows access to products info and the data related to the current customer.
// It also has scope to create anonymous tokens. But to do that basic auth is needed and the end user only has the access token and not the username and password for the API client.
// The access token can be read in the X-ct-access-token header for requetsts to the BFF.
const commerceToolsApiClient = process.env.COMMERCETOOLS_APPLICATION_CLIENT_ID
    ? generateSDKAuthClient(appClientScopes)
    : null;

const ERROR_INVALID_MESSAGE = "Invalid attributes";

// This request will be used on an API route, so we won't use React Query here.
export const signUp = async ({
    accessToken,
    consentVersions,
    email,
    firstName,
    lastName,
    locale,
    market,
    password,
    dateOfBirth,
    phoneticFirstName,
    phoneticLastName,
}: SignUpValues): Promise<ApiCustomerResponse> => {
    if (!email || !password) throw new Error(ERROR_INVALID_MESSAGE);
    const signUpEndpoint = `${process.env.STORE_FRONT_API_URL}/account/signup`;
    const body = {
        firstName,
        lastName,
        email,
        password,
        storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
        market: market,
        signUpSource: "Web",
        locale,
        consentVersions,
        dateOfBirth,
        phoneticFirstName,
        phoneticLastName,
    };

    return await FetchServiceAxios.post<any, ApiCustomerResponse>(signUpEndpoint, body, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
};

export const signIn = async ({
    email,
    password,
    locale,
    accessToken,
}: Credentials): Promise<LoginType> => {
    if (!email || !password) throw new Error(ERROR_INVALID_MESSAGE);
    const defaultLocale = process.env.NEXT_LOCALES?.split(",")[0];

    // By calling this method we get info on the customer.
    // Also if there is a cart for the anonymous session, it will be merged with the customer's cart.
    const signInEndpoint = `${process.env.STORE_FRONT_API_URL}/account/me/signin`;
    const body = {
        email,
        password,
        storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
        locale: locale ?? defaultLocale,
    };

    return await FetchServiceAxios.post<LoginType, any>(signInEndpoint, body, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
};

// We're missing types from the commercetools/sdk-auth package so we have to mock it.
type AnonymousFlowToken = {
    access_token: string;
    expires_in: number;
    token_type: string;
    scope: string;
    refresh_token: string;
    expires_at: number;
};
export const createAnonymousCommercetoolsToken = async (): Promise<AnonymousFlowToken | null> => {
    if (!commerceToolsApiClient) return null;
    return await commerceToolsApiClient.anonymousFlow();
};

export const getCommercetoolsTokenFromSession = async (
    req: NextApiRequest
): Promise<string | null> => {
    const session = await getToken({ req });
    if (session && typeof session["accessToken"] === "string") {
        return session["accessToken"];
    }
    return null;
};

export const getTokensFromSession = async (req: NextApiRequest): Promise<JWT | null> => {
    const session = await getToken({ req });
    if (session) {
        return session;
    }
    return null;
};

export function generateSDKAuthClient(appClientScopes: string): SdkAuth {
    return new SdkAuth({
        host: COMMERCETOOLS_ACCESS_TOKEN_URL,
        projectKey: process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY,
        disableRefreshToken: false,
        credentials: {
            clientId: process.env.COMMERCETOOLS_APPLICATION_CLIENT_ID,
            clientSecret: process.env.COMMERCETOOLS_APPLICATION_CLIENT_SECRET,
        },
        timeout: 100000,
        scopes: [appClientScopes],
        fetch,
    });
}
